import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Users, MapPin, Sparkles, Globe2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { PageHeader, SearchBar, FilterChips, LoaderBlock, EmptyState } from '../components/listHelpers';

const COMMUNITY_CATEGORIES = [
  'Recreation',
  'Social',
  'Sports',
  'Cultural',
  'Professional',
  'Hobby',
  'Religious',
  'Educational',
];

function CommunityCard({ community, joined }) {
  const img = community.images?.[0];
  return (
    <Link
      to={`/app/communities/${community.id}`}
      data-testid={`community-card-${community.id}`}
      className="bg-white border border-[#F4F1DE] rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all"
    >
      <div className="relative aspect-[16/9] bg-[#FFE5DD]">
        {img ? (
          <img src={img} alt={community.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Users className="w-12 h-12 text-[#C8553D]/30" />
          </div>
        )}
        {community.is_featured && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#C8553D] text-white text-[9px] font-bold rounded uppercase flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5" />
            Featured
          </span>
        )}
        {joined && (
          <span className="absolute top-2 right-2 px-2 py-0.5 bg-[#2A9D8F] text-white text-[9px] font-bold rounded uppercase">
            Joined
          </span>
        )}
      </div>
      <div className="p-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#C8553D]">{community.category}</p>
        <h3 className="text-lg font-serif font-bold text-[#2B2D42] line-clamp-1 mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
          {community.name}
        </h3>
        <p className="text-xs text-[#6D6875] flex items-center gap-1 mt-0.5">
          <MapPin className="w-3 h-3" /> {community.city}
        </p>
        <p className="text-sm text-[#6D6875] line-clamp-2 mt-2">{community.description}</p>
        <div className="flex items-center justify-between mt-3 text-xs text-[#6D6875]">
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" /> {(community.member_count || 0).toLocaleString()} members
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Communities() {
  const [communities, setCommunities] = useState([]);
  const [joinedIds, setJoinedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await api('/communities/joined/list');
        setJoinedIds(new Set((data.communities || []).map((c) => c.id)));
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (category) params.set('category', category.toLowerCase());
        if (city) params.set('city', city);
        const data = await api(`/communities?${params.toString()}`);
        setCommunities(data.communities || []);
      } catch (err) {
        toast.error(err.message || 'Failed to load communities');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, category, city]);

  const cities = useMemo(() => {
    const s = new Set();
    communities.forEach((c) => c.city && s.add(c.city));
    return Array.from(s).sort();
  }, [communities]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="communities-page">
      <PageHeader title="Communities" subtitle="Find your tribe — join groups by interest, city, or culture" testId="communities-header" />

      <SearchBar value={search} onChange={setSearch} placeholder="Search communities, activities..." testId="communities-search" />

      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#6D6875]">Category</p>
        <FilterChips items={COMMUNITY_CATEGORIES} value={category} onChange={setCategory} testIdPrefix="communities-cat" />
      </div>

      {cities.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#6D6875]">City</p>
          <FilterChips items={cities} value={city} onChange={setCity} testIdPrefix="communities-city" />
        </div>
      )}

      {loading ? (
        <LoaderBlock testId="communities-loading" />
      ) : communities.length === 0 ? (
        <EmptyState icon={Users} title="No communities found" sub="Try a different filter or check back soon." testId="communities-empty" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2" data-testid="communities-grid">
          {communities.map((c) => (
            <CommunityCard key={c.id} community={c} joined={joinedIds.has(c.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
